import { useCallback, useMemo } from "react"
import { createStore, createEffect } from "effector"
import { useUnit } from "effector-react"

import InterfaceStatus, { ConnectedSystem, InterfaceTopic, InterfaceType } from "models/InterfaceStatus"
import DataService from "services/DataService"
import toastMessages from "./toastMessages"

// Stores
const $interfaceStatuses = createStore<InterfaceStatus[] | null>(null)

// Effects
const fetchInterfaceStatuses = createEffect(async (): Promise<InterfaceStatus[]> => {
    try {
        return await DataService.getInterfaceStatuses()
    } catch (e) {
        toastMessages.showSystemError()
        return []
    }
})

// Listeners
$interfaceStatuses.on(fetchInterfaceStatuses.doneData, (_, payload) => payload)

// Exports
export default {
    $interfaceStatuses,
    fetchInterfaceStatuses,
}

export function useInterfaceStatuses() {
    const interfaceStatuses = useUnit($interfaceStatuses)

    const isInterfaceConnected = useCallback(({
        topic,
        type,
        connectedSystem,
    }: {
        topic: InterfaceTopic
        type: InterfaceType
        connectedSystem?: ConnectedSystem
    }) => {
        if (!interfaceStatuses) {
            return false
        }

        return interfaceStatuses.some(is => {
            return is.topic === topic
                && is.type === type
                && (connectedSystem ? is.connectedSystem === connectedSystem : true)
                && is.isConnected
        })
    }, [interfaceStatuses])

    const isInvoiceERPInterfaceConnected = useMemo(() => {
        return isInterfaceConnected({
            topic: InterfaceTopic.salesOrder,
            type: InterfaceType.write,
        }) && isInterfaceConnected({
            topic: InterfaceTopic.salesOrder,
            type: InterfaceType.adhoc,
        })
    }, [isInterfaceConnected])

    const isImportInvoiceERPInterfaceConnected = useMemo(() => {
        return isInterfaceConnected({
            topic: InterfaceTopic.invoice,
            type: InterfaceType.read
        })
    }, [isInterfaceConnected])

    return {
        interfaceStatuses: interfaceStatuses || [],
        isInterfaceConnected,
        isInvoiceERPInterfaceConnected,
        isImportInvoiceERPInterfaceConnected
    }
}
